import React, { useEffect, useMemo, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker as DatePickerBase } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { joinCampaignHelper } from "@/features/campaigns/shared/helpers/joinCampaignHelper";
import { CAMPAIGN_CONST } from "@/config/constants";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { createDatePickerInputClickHandler } from "@/helpers/utils";

const DatePicker = DatePickerBase as unknown as React.ComponentType<
  Record<string, unknown>
>;

export default function LocationDate({
  onSelectStartDate,
  onSelectEndDate,
  start_date,
  end_date,
  campaign,
  loc,
  canChangeDate,
  initialDisabled,
}) {
  const [startDate, setStartDate] = useState(
    start_date ? moment(start_date) : null
  );
  const [endDate, setEndDate] = useState(end_date ? moment(end_date) : null);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  useEffect(() => {
    const { campaign_type, evergreen_participant_date_flag } = campaign;
    if (start_date) {
      let sDate = moment(start_date);
      if (
        campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN &&
        evergreen_participant_date_flag
      ) {
        const { minStartDate } = joinCampaignHelper.getCampaignMinMaxStartDate(
          loc,
          campaign
        );

        if (sDate.isBefore(minStartDate)) {
          sDate = minStartDate;
        }
      }
      setStartDate(sDate);
    }
  }, [start_date, campaign, loc?.subscription?.subscription_end_date]);

  const dateConstraints = useMemo(() => {
    const {
      campaign_type,
      evergreen_participant_date_flag,
      // evergreen_participant_min_value,
    } = campaign;

    let minStartDate;
    let maxStartDate;
    let minDate;
    let maxDate;
    let disabled = true;

    if (
      campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN &&
      evergreen_participant_date_flag
    ) {
      const { minStartDate: minS, maxStartDate: maxS } =
        joinCampaignHelper.getCampaignMinMaxStartDate(loc, campaign);

      minStartDate = minS;
      maxStartDate = maxS;

      const { minEndDate, maxEndDate } =
        joinCampaignHelper.getCampaignMinMaxEndDate(
          startDate || minStartDate,
          loc,
          campaign
        );

      minDate = minEndDate;
      maxDate = maxEndDate;

      disabled = false;
    }

    return {
      minStartDate,
      maxStartDate,
      minDate,
      maxDate,
      disabled,
    };
  }, [campaign, startDate]);

  const handleStartDateChange = (d) => {
    setStartDate(moment(d));
    onSelectStartDate(moment(d));

    const { campaign_type, evergreen_participant_date_flag } = campaign;

    if (endDate && moment(endDate).isValid()) {
      const { minEndDate, maxEndDate } =
        joinCampaignHelper.getCampaignMinMaxEndDate(d, loc, campaign);

      if (
        campaign_type === CAMPAIGN_CONST.TYPE.EVERGREEN &&
        evergreen_participant_date_flag
      ) {
        if (endDate.isBefore(minEndDate, "day")) {
          setEndDate(minEndDate);
          onSelectEndDate(minEndDate, d);
        } else if (endDate.isAfter(maxEndDate, "day")) {
          setEndDate(maxEndDate);
          onSelectEndDate(maxEndDate, d);
        }
      } else if (endDate.isBefore(d, "day")) {
        setEndDate(d);
        onSelectEndDate(d, d);
      }
    }
  };

  const handleEndDateChange = (d) => {
    setEndDate(d);
    onSelectEndDate(d);
  };
  return (
    <div className="location-date">
      <label>{canChangeDate ? "Select " : ""}Campaign Dates</label>
      <LocalizationProvider
        dateAdapter={AdapterMoment}
        localeText={{
          fieldMonthPlaceholder: () => "",
          fieldDayPlaceholder: () => "",
          fieldYearPlaceholder: () => "",
        }}
      >
        <div className="row">
          <div className="col-sm-6">
            <div className="rp-date-input-box">
              <DatePicker
                showDaysOutsideCurrentMonth
                disableHighlightToday
                value={startDate}
                format="MMM Do YYYY"
                label={!startDate ? "Start Date" : ""}
                onChange={handleStartDateChange}
                fullWidth
                views={["day"]}
                disabled={initialDisabled || dateConstraints?.disabled}
                className="rp-date-picker"
                minDate={dateConstraints?.minStartDate}
                maxDate={dateConstraints?.maxStartDate}
                id={`start_date_${loc.id}`}
                open={startDateOpen}
                onOpen={() => setStartDateOpen(true)}
                onClose={() => setStartDateOpen(false)}
                slots={{
                  openPickerIcon: CalendarMonthIcon,
                  toolbar: () => null,
                }}
                FormHelperTextProps={{
                  style: {
                    position: "absolute",
                    top: "95%",
                    lineHeight: 1,
                  },
                }}
                slotProps={{
                  textField: {
                    readOnly: true,
                    variant: "outlined",
                    fullWidth: true,
                    size: "small",
                    disabled: initialDisabled || dateConstraints.disabled,
                    onClick: createDatePickerInputClickHandler(
                      () => setStartDateOpen(true),
                      initialDisabled || dateConstraints.disabled,
                      startDateOpen
                    ),
                    InputLabelProps: {
                      shrink: false,
                      sx: {
                        color: "var(--palette-text-secondary)",
                        fontSize: "12px",
                        fontWeight: "600",
                        "&.Mui-focused": {
                          color: "var(--palette-text-secondary)",
                        },
                        "&.MuiInputLabel-shrink": {
                          color: "var(--palette-text-secondary)",
                        },
                      },
                    },
                    InputProps: {
                      readOnly: true,
                      sx: {
                        "& input": {
                          cursor: "pointer",
                        },
                      },
                    },
                    sx: (theme) => ({
                      backgroundColor:
                        initialDisabled || dateConstraints.disabled
                          ? theme.palette.action.disabledBackground
                          : theme.palette.background.paper,
                      borderRadius: "var(--radius-control)",
                      border: "none",
                      "& .MuiPickersOutlinedInput-root": {
                        height: "40px",
                        fontSize: "12px !important",
                        color: "var(--palette-text-primary) !important",
                        fontWeight: "500 !important",
                      },
                    }),
                  },
                  openPickerButton: {
                    disabled: initialDisabled || dateConstraints.disabled,
                    sx: (theme) => ({
                      color:
                        initialDisabled || dateConstraints.disabled
                          ? theme.palette.action.disabled
                          : theme.palette.primary.main,
                    }),
                  },
                  day: {
                    sx: {
                      "&.MuiPickersDay-daySelected": {
                        color: "var(--palette-primary-contrastText) !important",
                        border: "none !important",
                        backgroundColor: "var(--primary-base) !important",
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
          <div className="col-sm-6 py-2 py-md-0">
            <div className="rp-date-input-box">
              <DatePicker
                showDaysOutsideCurrentMonth
                disableHighlightToday
                value={endDate}
                format="MMM Do YYYY"
                label={!endDate ? "End Date" : ""}
                onChange={handleEndDateChange}
                fullWidth
                views={["day"]}
                open={endDateOpen}
                onOpen={() => setEndDateOpen(true)}
                onClose={() => setEndDateOpen(false)}
                slots={{
                  openPickerIcon: CalendarMonthIcon,
                  toolbar: () => null,
                }}
                slotProps={{
                  textField: {
                    readOnly: true,
                    variant: "outlined",
                    fullWidth: true,
                    disabled:
                      initialDisabled || dateConstraints.disabled || !startDate,
                    onClick: createDatePickerInputClickHandler(
                      () => setEndDateOpen(true),
                      initialDisabled || dateConstraints.disabled || !startDate,
                      endDateOpen
                    ),
                    InputLabelProps: {
                      shrink: false,
                      sx: {
                        color: "var(--palette-text-secondary)",
                        fontSize: "12px",
                        fontWeight: "600",
                        "&.Mui-focused": {
                          color: "var(--palette-text-secondary)",
                        },
                        "&.MuiInputLabel-shrink": {
                          color: "var(--palette-text-secondary)",
                        },
                      },
                    },
                    InputProps: {
                      readOnly: true,
                      sx: {
                        "& input": {
                          cursor: "pointer",
                        },
                      },
                    },
                    size: "small",
                    sx: (theme) => ({
                      backgroundColor:
                        initialDisabled ||
                        dateConstraints.disabled ||
                        !startDate
                          ? theme.palette.action.disabledBackground
                          : theme.palette.background.paper,
                      borderRadius: "var(--radius-control)",
                      border: "none",
                      "& .MuiPickersOutlinedInput-root": {
                        height: "40px",
                        fontSize: "12px !important",
                        color: "var(--palette-text-primary) !important",
                        fontWeight: "500 !important",
                      },
                    }),
                  },
                  openPickerButton: {
                    disabled:
                      initialDisabled || dateConstraints.disabled || !startDate,
                    sx: (theme) => ({
                      color:
                        initialDisabled ||
                        dateConstraints.disabled ||
                        !startDate
                          ? theme.palette.action.disabled
                          : theme.palette.primary.main,
                    }),
                  },
                  day: {
                    sx: {
                      "&.MuiPickersDay-daySelected": {
                        color: "var(--palette-primary-contrastText) !important",
                        border: "none !important",
                        backgroundColor: "var(--primary-base) !important",
                      },
                    },
                  },
                }}
                className="rp-date-picker"
                disabled={
                  initialDisabled || dateConstraints?.disabled || !startDate
                }
                minDate={dateConstraints?.minDate}
                maxDate={dateConstraints?.maxDate}
                minDateMessage=""
              />
            </div>
          </div>
        </div>
      </LocalizationProvider>
    </div>
  );
}
